import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { ActionInfo, ProductThumb } from '@krp/ui-kit'

import { thumbOf, type ProductPhotoUrl, type ProductRef } from './product'
import './platform.css'

export interface ProductTileLine {
  label: string
  value: ReactNode
}

/** Сколько строк названия видно в плашке; остальное обрезается многоточием. */
const NAME_LINES = 3

/**
 * Плашка найденного товара — одна на поиск по фото и штрихкод сканера: нажатие на фото открывает его на весь экран,
 * нажатие на название — ОБЩУЮ карточку товара (`onOpen`; решение владельца 24.10.2026: товар, найденный в общем каталоге, открывается
 * общей карточкой, а не карточкой одного из источников). Раскладка (23.10.2026): слева миниатюра фиксированного размера,
 * справа название не длиннее {@link NAME_LINES} строк, под ними мелко — артикул и штрихкод. В правом верхнем углу — только
 * число с процентом совпадения (`matchPercent`), без слов; место под него у названия зарезервировано. Миниатюра — главное фото общего
 * товара (`commonPhotoUrl`); запасное фото по первой карточке товара в источнике, у вида которой оно есть, — только когда общего фото нет.
 */
export function ProductTile({
  name,
  refs,
  lines,
  photoUrl,
  commonPhotoUrl,
  matchPercent,
  onOpen,
  action,
}: {
  name: string
  refs: readonly ProductRef[]
  lines: readonly ProductTileLine[]
  photoUrl?: ProductPhotoUrl | undefined
  /** Миниатюра главного фото ОБЩЕГО товара (у Platform); задана — показывается она, а `photoUrl` по карточкам источников — только когда её нет. */
  commonPhotoUrl?: string | null | undefined
  /** Процент совпадения целым числом; `null` или не задан — метки нет. Вне кнопки и нажатие не перехватывает. */
  matchPercent?: number | null | undefined
  /** Открыть общую карточку этого товара: название плашки — всегда вход в неё. */
  onOpen: () => void
  /** Кнопка под строками плашки («Это он» в ответе поиска по фото); не задана — плашка без кнопок. */
  action?: ReactNode
}) {
  const photo = thumbOf(commonPhotoUrl, refs, photoUrl)
  const hasMatch = matchPercent !== undefined && matchPercent !== null
  const title = (
    <span className="platform-product__name" data-lines={NAME_LINES} title={name}>
      {name}
    </span>
  )
  return (
    <li className="platform-product" data-badge={hasMatch ? 'true' : undefined}>
      <ActionInfo id="platform.search.product-open" layout="block" label={name}>
        <div className="platform-product__row">
          <ProductThumb src={photo} name={name} />
          <button type="button" className="platform-product__main" data-open="true" onClick={onOpen}>
            {title}
            <ChevronRight className="platform-product__go" size={18} aria-hidden="true" />
          </button>
        </div>
      </ActionInfo>
      {lines.length === 0 ? null : (
        <p className="platform-product__lines">
          {lines.map((line) => (
            <span key={line.label} className="platform-product__line">
              <span className="platform-product__label">{line.label}</span> {line.value}
            </span>
          ))}
        </p>
      )}
      {action !== undefined && action !== null && <div className="platform-product__action">{action}</div>}
      {hasMatch && (
        <span className="platform-product__match" role="img" aria-label={`совпадение ${matchPercent}%`}>
          {matchPercent}%
        </span>
      )}
    </li>
  )
}
